import type React from "react";
import i18n from "../i18n";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {Link, useLocation, useNavigate, useParams} from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LockIcon from "@mui/icons-material/Lock";
import {SmsSenderRegistrationsCard} from "../components/sms-sender/SmsSenderRegistrationsCard";
import {useAppDispatch, useAppSelector} from "../store/hooks";
import {fetchPlans, plansSelectors} from "../store/plansSlice";
import {fetchTenants, removeTenantFromCache, updateTenantInCache} from "../store/tenantsSlice";
import {deleteTenant, getEmployees, getTenantById, updateTenant} from "../api/tenantDetailApi";
import {getGlobalFeatureFlags} from "../api/configurationApi";
import type {Employee, GlobalFeatureFlags, TenantDetailsResponse, TenantFeatureConfig} from "../types/tenant";
import {PageHeader} from "../components/ui/PageHeader";
import {UnsavedChangesGuard} from "../components/ui/UnsavedChangesGuard";
import {SectionCard} from "../components/ui/SectionCard";
import {KeyHint, StatusChip} from "../components/ui/StatusChip";
import {SmsProviderSelect, TenantFeatureFlagsEditor} from "../components/feature-flags/TenantFeatureFlagsEditor";
import {SubscriptionsSection} from "../components/tenant-detail/SubscriptionsSection";
import {QuotasSection} from "../components/tenant-detail/QuotasSection";
import {MaintenanceSection} from "../components/tenant-detail/MaintenanceSection";
import {TENANT_FORM_FEATURE_FLAGS} from "../constants/featureFlags";
import {featureFlagLabel} from "../constants/featureFlagLabels";
import {summarizeOverrides} from "../utils/featureFlagResolution";
import {buildTenantUpdateRequest, type GeneralChange, type GeneralForm, listGeneralChanges} from "../utils/tenantEditForm";
import {getSubscriptionAccessEndDate} from "../utils/subscriptionDates";
import type {TernaryFlagValue} from "../components/feature-flags/TernaryFeatureFlag";
import {colors, fonts, layout} from "../theme/tokens";
import {formatDate} from "../util/format";

const SECTIONS = [
    {id: "basic-information", labelKey: "DETAIL_BASIC_INFORMATION"},
    {id: "subscriptions", labelKey: "DETAIL_SUBSCRIPTIONS"},
    {id: "features", labelKey: "DETAIL_FEATURES"},
    {id: "quotas", labelKey: "DETAIL_QUOTAS"},
    {id: "sms-sender-registrations", labelKey: "SMS_SENDER_REGISTRATIONS"},
    {id: "support", labelKey: "NAV_SUPPORT"},
    {id: "maintenance", labelKey: "DETAIL_MAINTENANCE"},
] as const;

type SectionId = (typeof SECTIONS)[number]["id"];

function sectionNumber(id: SectionId): number {
    return SECTIONS.findIndex((section) => section.id === id) + 1;
}

function subscriptionExpired(tenant: TenantDetailsResponse): boolean {
    const subscription = tenant.activeSubscription;
    if (!subscription) return true;
    return getSubscriptionAccessEndDate(subscription).getTime() < new Date().getTime();
}

function generalFormFrom(tenant: TenantDetailsResponse, storedFeatureConfig: TenantFeatureConfig): GeneralForm {
    return {
        name: tenant.name,
        notes: tenant.notes ?? "",
        administratorEmployeeId: tenant.administrator?.id ?? "",
        featureConfig: {...storedFeatureConfig},
        blockList: tenant.blockList ?? [],
        assignedSupportEngineerIds: tenant.assignedSupportEngineerIds ?? [],
    };
}

/** Login-enabled, non-deleted employees of this tenant; the current administrator is always selectable. */
function administratorChoices(tenant: TenantDetailsResponse): Employee[] {
    const choices: Employee[] = (tenant.employees ?? []).filter((employee) => employee.canLogin && !employee.deleted);
    const current = tenant.administrator;
    if (current && !choices.some((employee) => employee.id === current.id)) return [current, ...choices];
    return choices;
}

/** What another page can hand over when it navigates here, e.g. a creation that only partly succeeded. */
export type TenantDetailNavigationState = {notice: string};

function navigationNotice(state: unknown): string | null {
    if (typeof state !== "object" || state === null || !("notice" in state)) return null;
    return typeof state.notice === "string" ? state.notice : null;
}

type LoadedState = {
    tenant: TenantDetailsResponse;
    engineers: Employee[];
    storedFlagsUnavailable: boolean;
};

export function TenantDetailPage() {
    const {t} = useTranslation();
    const {tenantId} = useParams<{tenantId: string}>();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const plans = useAppSelector(plansSelectors.selectAll);
    const location = useLocation();
    const [handedOverNotice, setHandedOverNotice] = useState<string | null>(() => navigationNotice(location.state));

    const [loaded, setLoaded] = useState<LoadedState | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [globalFlags, setGlobalFlags] = useState<GlobalFeatureFlags | null>(null);
    const [original, setOriginal] = useState<GeneralForm | null>(null);
    const [form, setForm] = useState<GeneralForm | null>(null);
    const [saveStatus, setSaveStatus] = useState<"idle" | "loading">("idle");
    const [saveError, setSaveError] = useState<string | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    // Set to leave on purpose (after deleting the tenant) without the unsaved-changes question.
    const [leaveTo, setLeaveTo] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const latestLoad = useRef(0);

    const load = useCallback(async () => {
        if (!tenantId) return;
        const request = ++latestLoad.current;
        setLoaded(null);
        setLoadError(null);
        setOriginal(null);
        setForm(null);
        setSaveError(null);
        try {
            // Tenant detail returns flags resolved against the global defaults; the summary list holds the stored
            // overrides, which are what the form edits so that inheritance survives a save.
            const [tenant, engineers, summaries] = await Promise.all([
                getTenantById(tenantId),
                getEmployees(),
                dispatch(fetchTenants()),
            ]);
            if (request !== latestLoad.current) return;
            const summary = fetchTenants.fulfilled.match(summaries) ? summaries.payload.find((candidate) => candidate.id === tenantId) : undefined;
            const storedFeatureConfig = summary ? (summary.featureConfig ?? {}) : (tenant.featureConfig ?? {});
            const initial = generalFormFrom(tenant, storedFeatureConfig);
            setLoaded({tenant, engineers: engineers ?? [], storedFlagsUnavailable: !summary});
            setOriginal(initial);
            setForm(initial);
        } catch (e) {
            if (request !== latestLoad.current) return;
            setLoadError(e instanceof Error ? e.message : i18n.t("DETAIL_LOAD_TENANT_ERROR"));
        }
    }, [tenantId, dispatch]);

    useEffect(() => {
        void load();
    }, [load]);

    /** Refreshes tenant data after an action that persisted on its own, keeping unsaved general edits. */
    const reloadTenantData = useCallback(async () => {
        if (!tenantId) return;
        try {
            const tenant = await getTenantById(tenantId);
            setLoaded((current) => (current ? {...current, tenant} : current));
        } catch (e) {
            setSaveError(e instanceof Error ? e.message : i18n.t("DETAIL_LOAD_TENANT_ERROR"));
        }
        void dispatch(fetchTenants());
    }, [tenantId, dispatch]);

    useEffect(() => {
        let active = true;
        getGlobalFeatureFlags()
                .then((flags) => {
                    if (active) setGlobalFlags(flags);
                })
                .catch(() => {
                    if (active) setGlobalFlags(null);
                });
        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        if (plans.length === 0) void dispatch(fetchPlans());
    }, [dispatch, plans.length]);

    const changes = useMemo(() => (original && form ? listGeneralChanges(original, form) : []), [original, form]);
    const dirty = changes.length > 0;

    useEffect(() => {
        if (leaveTo) navigate(leaveTo);
    }, [leaveTo, navigate]);

    const handleSave = useCallback(async () => {
        if (!tenantId || !form || !original || !form.name.trim()) return;
        setSaveStatus("loading");
        setSaveError(null);
        try {
            const request = buildTenantUpdateRequest(form, original.administratorEmployeeId);
            const updated = await updateTenant(tenantId, request);
            const saved: GeneralForm = {
                ...form,
                name: request.name,
                notes: request.notes ?? "",
                blockList: request.blockList ?? [],
            };
            setLoaded((current) => (current ? {...current, tenant: updated} : current));
            // The submitted values become the baseline; edits made while the request ran stay in the form as changes.
            setOriginal(saved);
            setForm((current) => (current === form ? saved : current));
            dispatch(updateTenantInCache({
                id: tenantId,
                changes: {
                    name: request.name,
                    notes: request.notes,
                    featureConfig: request.featureConfig,
                    assignedSupportEngineerIds: request.assignedSupportEngineerIds,
                },
            }));
        } catch (e) {
            setSaveError(e instanceof Error ? e.message : t("DETAIL_SAVE_FAILED"));
        } finally {
            setSaveStatus("idle");
        }
    }, [tenantId, form, original, dispatch, t]);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (document.querySelector(".MuiDialog-root")) return;
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
                event.preventDefault();
                if (dirty && saveStatus === "idle") void handleSave();
            }
            if (event.key === "Escape") navigate("/tenants");
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [dirty, saveStatus, handleSave, navigate]);

    const handleDelete = async () => {
        if (!tenantId) return;
        try {
            await deleteTenant(tenantId);
            setDeleteDialogOpen(false);
            dispatch(removeTenantFromCache(tenantId));
            setLeaveTo("/tenants");
        } catch (e) {
            setDeleteDialogOpen(false);
            setSaveError(e instanceof Error ? e.message : t("DETAIL_DELETE_FAILED"));
        }
    };

    const copyTenantId = (id: string) => {
        void navigator.clipboard?.writeText(id);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
    };

    const breadcrumbs = (name?: string) => [
        {label: t("NAV_TENANTS"), to: "/tenants", ariaLabel: t("DETAIL_BREADCRUMB_TENANTS_ARIA")},
        ...(name ? [{label: name}] : []),
    ];

    if (loadError) {
        return (
                <>
                    <PageHeader title={t("DETAIL_EDIT_TENANT")} breadcrumbs={breadcrumbs()} />
                    <Alert severity="error">{loadError || t("DETAIL_TENANT_NOT_FOUND")}</Alert>
                </>
        );
    }

    if (!loaded || !form || !original) {
        return (
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", minHeight: 240}}>
                    <CircularProgress />
                </Box>
        );
    }

    const {tenant, engineers, storedFlagsUnavailable} = loaded;
    const setField = <K extends keyof GeneralForm>(key: K, value: GeneralForm[K]) => setForm({...form, [key]: value});
    const adminChoices = administratorChoices(tenant);
    const selectedAdministrator = adminChoices.find((employee) => employee.id === form.administratorEmployeeId);
    const flagSummary = summarizeOverrides(form.featureConfig, TENANT_FORM_FEATURE_FLAGS);
    const canDeleteTenant = subscriptionExpired(tenant);
    const loginEnabled = Boolean(tenant.administratorCognitoUser?.enabled);
    const activeEngineers = engineers.filter((engineer) => !engineer.deleted);

    const describeChange = (change: GeneralChange): string => {
        const ternary = (value: TernaryFlagValue) =>
                value === true ? t("FLAGS_TERNARY_ON_SHORT") : value === false ? t("FLAGS_TERNARY_OFF_SHORT") : t("FLAGS_TERNARY_INHERIT_SHORT");
        const employeeName = (employeeId: string) =>
                [...adminChoices, ...engineers].find((employee) => employee.id === employeeId)?.alias ?? (employeeId || "-");
        switch (change.kind) {
            case "name":
                return t("DETAIL_CHANGE_NAME", {from: change.from, to: change.to});
            case "notes":
                return t("DETAIL_CHANGE_NOTES");
            case "administrator":
                return t("DETAIL_CHANGE_ADMINISTRATOR", {from: employeeName(change.fromId), to: employeeName(change.toId)});
            case "flag":
                return t("DETAIL_CHANGE_FLAG", {flag: featureFlagLabel(t, change.flag), from: ternary(change.from), to: ternary(change.to)});
            case "smsProvider":
                return t("DETAIL_CHANGE_SMS_PROVIDER", {from: change.from ?? "-", to: change.to ?? "-"});
            case "blocklistAdded":
                return t("DETAIL_CHANGE_BLOCKLIST_ADDED", {entry: change.entry});
            case "blocklistRemoved":
                return t("DETAIL_CHANGE_BLOCKLIST_REMOVED", {entry: change.entry});
            case "engineerAdded":
                return t("DETAIL_CHANGE_ENGINEER_ADDED", {name: employeeName(change.employeeId)});
            case "engineerRemoved":
                return t("DETAIL_CHANGE_ENGINEER_REMOVED", {name: employeeName(change.employeeId)});
        }
    };

    return (
            <>
                <PageHeader
                        breadcrumbs={breadcrumbs(tenant.name)}
                        title={t("DETAIL_EDIT_TENANT")}
                        titleAdornment={
                            <>
                                <ButtonBase
                                        onClick={() => copyTenantId(tenant.id)}
                                        aria-label={t("DETAIL_COPY_TENANT_ID")}
                                        sx={{
                                            gap: 0.75,
                                            height: 26,
                                            px: 1,
                                            borderRadius: "4px",
                                            border: `1px solid ${colors.cardBorder}`,
                                            backgroundColor: "#fff",
                                            fontFamily: fonts.mono,
                                            fontSize: "12px",
                                            color: colors.body,
                                            "&:hover": {borderColor: colors.inputBorder},
                                        }}
                                >
                                    <span>{t("DETAIL_TENANT_ID")}: {tenant.id}</span>
                                    <ContentCopyIcon sx={{fontSize: 14, color: colors.faint}} />
                                </ButtonBase>
                                <Box component="span" role="status" sx={{fontSize: "12px", color: colors.success}}>
                                    {copied ? t("DETAIL_COPIED") : ""}
                                </Box>
                                <Box
                                        component="span"
                                        title={`Status: ${tenant.administratorCognitoUser?.userStatus ?? ""}\nCognito: ${tenant.administratorCognitoUser?.userName ?? ""}\ncanLogin: ${tenant.administrator?.canLogin ?? ""}`}
                                        sx={{display: "inline-flex", alignItems: "center", gap: 0.75, fontSize: "12.5px", color: colors.muted}}
                                >
                                    {t("DETAIL_ADMIN_LOGIN")}
                                    <StatusChip tone={loginEnabled ? "success" : "neutral"}>
                                        {loginEnabled ? t("DETAIL_ENABLED") : t("DETAIL_DISABLED")}
                                    </StatusChip>
                                </Box>
                            </>
                        }
                />

                {handedOverNotice && (
                        <Alert severity="warning" onClose={() => setHandedOverNotice(null)}>{handedOverNotice}</Alert>
                )}

                {saveError !== null && (
                        <Alert severity="error" onClose={() => setSaveError(null)}>
                            {saveError || t("DETAIL_ERROR_FALLBACK")}
                        </Alert>
                )}

                <Box sx={{display: "grid", gridTemplateColumns: {xs: "minmax(0, 1fr)", lg: "minmax(0, 1fr) 340px"}, gap: "14px", alignItems: "start"}}>
                    <Box sx={{display: "flex", flexDirection: "column", gap: "14px", minWidth: 0}}>
                        <SectionCard id="basic-information" number={sectionNumber("basic-information")} title={t("DETAIL_BASIC_INFORMATION")}>
                            <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", md: "repeat(3, 1fr)"}, gap: 2}}>
                                <TextField
                                        label={t("TENANTS_COLUMN_NAME")}
                                        required
                                        size="small"
                                        value={form.name}
                                        onChange={(e) => setField("name", e.target.value)}
                                        error={!form.name.trim()}
                                        slotProps={{htmlInput: {maxLength: 256}}}
                                />
                                <FormControl size="small" fullWidth>
                                    <InputLabel id="detail-admin-label">{t("DETAIL_ADMINISTRATOR")}</InputLabel>
                                    <Select
                                            labelId="detail-admin-label"
                                            id="detail-admin"
                                            value={form.administratorEmployeeId}
                                            label={t("DETAIL_ADMINISTRATOR")}
                                            onChange={(e) => setField("administratorEmployeeId", e.target.value)}
                                    >
                                        <MenuItem value="">{t("DETAIL_NO_SELECTION")}</MenuItem>
                                        {adminChoices.map((employee) => (
                                                <MenuItem key={employee.id} value={employee.id}>
                                                    {employee.alias}
                                                    {employee.emailAddress && (
                                                            <Box component="span" sx={{ml: 1, color: colors.faint}}>({employee.emailAddress})</Box>
                                                    )}
                                                </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TextField
                                        label={t("DETAIL_ADMINISTRATOR_EMAIL_READONLY")}
                                        size="small"
                                        value={selectedAdministrator?.emailAddress ?? tenant.administrator?.emailAddress ?? ""}
                                        slotProps={{
                                            input: {
                                                readOnly: true,
                                                endAdornment: (
                                                        <InputAdornment position="end">
                                                            <LockIcon sx={{fontSize: 16, color: colors.faint}} />
                                                        </InputAdornment>
                                                ),
                                            },
                                        }}
                                />
                                <TextField
                                        label={t("DETAIL_NOTES")}
                                        size="small"
                                        multiline
                                        minRows={2}
                                        value={form.notes}
                                        onChange={(e) => setField("notes", e.target.value)}
                                        slotProps={{htmlInput: {maxLength: 50000}}}
                                        sx={{gridColumn: "1 / -1"}}
                                />
                            </Box>
                        </SectionCard>

                        <SubscriptionsSection
                                id="subscriptions"
                                number={sectionNumber("subscriptions")}
                                tenantId={tenant.id}
                                subscriptions={tenant.subscriptionHistory ?? []}
                                plans={plans}
                                onChanged={() => void reloadTenantData()}
                        />

                        <SectionCard
                                id="features"
                                number={sectionNumber("features")}
                                title={t("DETAIL_FEATURES")}
                                helper={t("FEATURE_SUMMARY", flagSummary)}
                                actions={<SmsProviderSelect id="detail-sms-provider" value={form.featureConfig} onChange={(config) => setField("featureConfig", config)} />}
                        >
                            {storedFlagsUnavailable && <Alert severity="info" sx={{mb: 2}}>{t("DETAIL_STORED_FLAGS_UNAVAILABLE")}</Alert>}
                            <TenantFeatureFlagsEditor value={form.featureConfig} onChange={(config) => setField("featureConfig", config)} globalFlags={globalFlags} />
                            <Typography variant="h6" component="h3" sx={{mt: 2.5}}>
                                {t("DETAIL_BLOCKLIST")}
                            </Typography>
                            <Typography variant="body2" sx={{color: colors.muted, mb: 1}}>
                                {t("DETAIL_BLOCKLIST_HINT")}
                            </Typography>
                            <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", md: "1fr 1fr"}, gap: 1.5}}>
                                {form.blockList.map((entry, index) => (
                                        <TextField
                                                key={index}
                                                size="small"
                                                placeholder={t("DETAIL_BLOCKLIST_PLACEHOLDER")}
                                                value={entry}
                                                onChange={(e) => setField("blockList", form.blockList.map((existing, i) => (i === index ? e.target.value : existing)))}
                                                slotProps={{
                                                    htmlInput: {"aria-label": t("DETAIL_BLOCKLIST"), style: {fontFamily: fonts.mono, fontSize: "12.5px"}},
                                                    input: {
                                                        endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <IconButton
                                                                            size="small"
                                                                            color="error"
                                                                            edge="end"
                                                                            onClick={() => setField("blockList", form.blockList.filter((_, i) => i !== index))}
                                                                            aria-label={t("DETAIL_REMOVE_BLOCKLIST_ENTRY")}
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </InputAdornment>
                                                        ),
                                                    },
                                                }}
                                        />
                                ))}
                            </Box>
                            <Button variant="text" startIcon={<AddIcon />} onClick={() => setField("blockList", [...form.blockList, ""])} sx={{mt: 1}}>
                                {t("ADD")}
                            </Button>
                        </SectionCard>

                        <QuotasSection id="quotas" number={sectionNumber("quotas")} tenantId={tenant.id} />

                        {/* Keyed so the list and any open dialog never carry over to another tenant. */}
                        <SmsSenderRegistrationsCard
                                key={tenant.id}
                                tenantId={tenant.id}
                                id="sms-sender-registrations"
                                number={sectionNumber("sms-sender-registrations")}
                                collapsible
                        />

                        <SectionCard
                                id="support"
                                number={sectionNumber("support")}
                                title={t("NAV_SUPPORT")}
                                actions={
                                    <Typography variant="body2" sx={{color: colors.muted}}>
                                        {t("DETAIL_SUPPORT_ACCESS")}:{" "}
                                        <Box component="span" sx={{color: tenant.publicDetails?.supportAccessGranted ? colors.success : colors.muted, fontWeight: 500}}>
                                            {tenant.publicDetails?.supportAccessGranted ? t("DETAIL_GRANTED") : t("DETAIL_NOT_GRANTED")}
                                        </Box>
                                    </Typography>
                                }
                        >
                            <Typography variant="body2" sx={{color: colors.muted, mb: 1}}>
                                {t("DETAIL_ASSIGNED_SUPPORT_ENGINEERS")}
                            </Typography>
                            <Box role="group" aria-label={t("DETAIL_ENGINEERS_ARIA")} sx={{display: "flex", flexWrap: "wrap", gap: 1}}>
                                {activeEngineers.map((engineer) => {
                                    const assigned = form.assignedSupportEngineerIds.includes(engineer.id);
                                    return (
                                            <ButtonBase
                                                    key={engineer.id}
                                                    role="checkbox"
                                                    aria-checked={assigned}
                                                    onClick={() =>
                                                            setField(
                                                                    "assignedSupportEngineerIds",
                                                                    assigned
                                                                            ? form.assignedSupportEngineerIds.filter((id) => id !== engineer.id)
                                                                            : [...form.assignedSupportEngineerIds, engineer.id],
                                                            )
                                                    }
                                                    sx={{
                                                        gap: 0.75,
                                                        height: 32,
                                                        px: 1.25,
                                                        borderRadius: "4px",
                                                        fontSize: "13px",
                                                        border: `1px solid ${assigned ? colors.primary : colors.inputBorder}`,
                                                        backgroundColor: assigned ? colors.infoTint : "#fff",
                                                        color: assigned ? colors.chrome : colors.body,
                                                        "& svg": {fontSize: 18, color: assigned ? colors.primary : colors.faint},
                                                    }}
                                            >
                                                {assigned ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                                                {engineer.alias}
                                            </ButtonBase>
                                    );
                                })}
                            </Box>
                        </SectionCard>

                        <MaintenanceSection id="maintenance" number={sectionNumber("maintenance")} tenantId={tenant.id} />
                    </Box>

                    <Box sx={{display: "flex", flexDirection: "column", gap: "14px", position: {lg: "sticky"}, top: layout.topBarHeight + 16}}>
                        <SectionCard title={t("DETAIL_UNSAVED_CHANGES")} headingLevel="h2">
                            {changes.length === 0 ? (
                                    <Typography variant="body2" sx={{color: colors.muted}}>{t("DETAIL_NO_UNSAVED_CHANGES")}</Typography>
                            ) : (
                                    <Box component="ul" sx={{m: 0, pl: 2, fontSize: "12.5px", color: colors.body, display: "flex", flexDirection: "column", gap: 0.5}}>
                                        {changes.map((change, index) => (
                                                <li key={index}>{describeChange(change)}</li>
                                        ))}
                                    </Box>
                            )}
                        </SectionCard>
                        <Box sx={{display: "flex", gap: 1}}>
                            <Button
                                    variant="contained"
                                    size="large"
                                    onClick={() => void handleSave()}
                                    disabled={saveStatus === "loading" || !dirty || !form.name.trim()}
                                    sx={{flex: 1}}
                            >
                                {saveStatus === "loading" ? t("DETAIL_SAVING") : dirty ? t("DETAIL_SAVE_CHANGES", {count: changes.length}) : t("DETAIL_SAVE")}
                                <KeyHint onDark>⌘S</KeyHint>
                            </Button>
                            <Button component={Link} to="/tenants" variant="outlined" size="large">
                                {t("CANCEL")}
                                <KeyHint>Esc</KeyHint>
                            </Button>
                        </Box>
                        <Paper variant="outlined" sx={{p: 1.5}}>
                            <Button
                                    color="error"
                                    variant="text"
                                    startIcon={<DeleteOutlineIcon />}
                                    disabled={!canDeleteTenant}
                                    onClick={() => setDeleteDialogOpen(true)}
                                    sx={{px: 0.5}}
                            >
                                {t("DETAIL_DELETE_TENANT")}
                            </Button>
                            {!canDeleteTenant && tenant.activeSubscription && (
                                    <Typography variant="caption" component="p" sx={{color: colors.muted, mt: 0.5}}>
                                        {t("DETAIL_DELETE_TENANT_UNAVAILABLE", {date: formatDate(tenant.activeSubscription.activeUntil)})}
                                    </Typography>
                            )}
                        </Paper>
                        <Paper variant="outlined" component="nav" aria-label={t("DETAIL_JUMP_TO")} sx={{py: 1.5}}>
                            <Typography variant="overline" component="div" sx={{px: 2, color: colors.faint}}>
                                {t("DETAIL_JUMP_TO")}
                            </Typography>
                            {SECTIONS.map((section, index) => (
                                    <ButtonBase
                                            key={section.id}
                                            onClick={() => document.getElementById(section.id)?.scrollIntoView({behavior: "smooth", block: "start"})}
                                            sx={{display: "flex", justifyContent: "flex-start", width: "100%", gap: 1.5, px: 2, py: 0.5, fontSize: "13px", color: colors.body, "&:hover": {backgroundColor: colors.navHover}}}
                                    >
                                        <Box component="span" aria-hidden sx={{color: colors.faint, fontSize: "11px", width: 10}}>{index + 1}</Box>
                                        <span>{t(section.labelKey)}</span>
                                    </ButtonBase>
                            ))}
                        </Paper>
                    </Box>
                </Box>

                <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
                    <DialogTitle>{t("DETAIL_DELETE_TENANT")}</DialogTitle>
                    <DialogContent>
                        <Typography>{t("DETAIL_DELETE_TENANT_CONFIRM")}</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="outlined" onClick={() => setDeleteDialogOpen(false)}>{t("CANCEL")}</Button>
                        <Button onClick={() => void handleDelete()} color="error" variant="contained">
                            {t("DELETE")}
                        </Button>
                    </DialogActions>
                </Dialog>

                <UnsavedChangesGuard
                        when={dirty && leaveTo === null}
                        title={t("DETAIL_DISCARD_TITLE")}
                        body={t("DETAIL_DISCARD_BODY", {count: changes.length})}
                        keepLabel={t("DETAIL_KEEP_EDITING")}
                        discardLabel={t("DETAIL_DISCARD_CONFIRM")}
                />
            </>
    );
}
